import { create } from "zustand";
import { BrokersStore, Broker, ServerHostPort, ProviderKind } from "./types";
import { getStoredBrokers, saveBrokers, clearStoredBrokers } from "./storage";
import {
  fetchCustomBrokers,
  fetchTradingGateway,
  mergeCustomBrokersIntoT5,
  useFetchBrokers,
} from "./hooks";

export const useBrokersStore = create<BrokersStore>((set, get) => ({
  brokers: getStoredBrokers(),
  isLoading: false,
  error: null,

  setBrokers: async () => {
    set({ isLoading: true, error: null });

    try {
      const [t5Brokers, customBrokers, gatewayBrokers] = await Promise.all([
        useFetchBrokers(),
        fetchCustomBrokers(),
        fetchTradingGateway(),
      ]);

      const mergedT5Brokers = mergeCustomBrokersIntoT5(t5Brokers, customBrokers);
      const fetchedBrokers = [...gatewayBrokers, ...mergedT5Brokers];
      set({
        brokers: fetchedBrokers,
        isLoading: false,
      });

      saveBrokers(fetchedBrokers);
    } catch (error) {
      set({
        isLoading: false,
        error:
          error instanceof Error ? error.message : "Failed to fetch brokers",
      });
    }
  },

  // Alias for backward compatibility
  fetchBrokers: async () => {
    return get().setBrokers();
  },

  clearBrokers: () => {
    set({ brokers: [] });
    clearStoredBrokers();
  },

  searchBrokers: (query: string): Broker[] => {
    // Only search if query has at least 2 characters (matches Add Account UI)
    if (query.length < 2) {
      return [];
    }

    const { brokers } = get();
    const lowerQuery = query.toLowerCase();

    return brokers.filter(
      (broker) =>
        broker.fullName.toLowerCase().includes(lowerQuery) ||
        broker.name.toLowerCase().includes(lowerQuery) ||
        broker.servers.some((server) =>
          server.serverName.toLowerCase().includes(lowerQuery)
        )
    );
  },

  getBrokerById: (id: string): Broker | undefined => {
    const { brokers } = get();
    return brokers.find((broker) => broker.id === id);
  },

  getBrokerByCompanyName: (companyName: string): Broker | undefined => {
    const { brokers } = get();
    return brokers.find(
      (broker) => broker.fullName === companyName || broker.name === companyName
    );
  },

  getServerByIdAndName: (brokerId: string, serverName: string) => {
    const broker = get().getBrokerById(brokerId);

    if (!broker) {
      return undefined;
    }

    return broker.servers.find((server) => server.serverName === serverName);
  },

  getServerHostPort: (
    id: string,
    serverName: string,
    providerKind: ProviderKind = "T5"
  ): ServerHostPort[] | undefined => {
    const broker = get().brokers.find(
      (candidate) =>
        candidate.providerKind === providerKind &&
        (candidate.id === id ||
          candidate.fullName === id ||
          candidate.name === id)
    );

    if (!broker) {
      return undefined;
    }

    const server = broker.servers.find(
      (candidate) => candidate.serverName === serverName
    );

    if (!server) {
      return undefined;
    }

    if (broker.providerKind === "Vybe") {
      return [{ host: "", port: 0 }];
    }

    return server.access.map((accessString) => {
      const [host, portStr] = accessString.split(":");
      const port = portStr ? parseInt(portStr, 10) : 443;

      return { host, port };
    });
  },

  /*
   * Example usage:
   * const hostPorts = getServerHostPort("Dom Maklerski Banku Ochrony Srodowiska S.A.", "BOSSAFX-Real");
   * // Returns: [
   * //   { host: "172.19.2.20", port: 443 },
   * //   { host: "195.8.97.76", port: 443 },
   * //   { host: "195.8.97.77", port: 443 },
   * //   { host: "195.8.97.78", port: 443 },
   * //   { host: "172.19.2.21", port: 443 }
   * // ]
   */

  loadBrokers: () => {
    const brokers = getStoredBrokers();
    set({ brokers });
  },
}));

// For backward compatibility with existing code
export const useBrokerStore = useBrokersStore;
